import { FC, useEffect, useState, useCallback, useMemo } from 'react';
import {
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Box,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { Document, Page, Text, View, Image } from '@react-pdf/renderer';
import { pdfStyles } from '../PdfPreview/styles';
import http from '../../../../../../apis/http';
import { DEFAULT_FUND_PARTNER_LABELS, DEFAULT_IMPORTANT_NOTES, SECTION_HEADER_DEFAULTS, SectionHeaders } from '../PdfPreview';
import moment from 'moment';
import { client } from '../../../../../../App';
import { GET_AUTOMATED_COLUMNS } from '../../../../../../queries/widgets';
import { normalizeNumberFormatLocale, NumberFormatLocale } from '../../../../../../utils/numberFormat';
import { dynamicCellCacheKey, dynamicFieldCacheKey } from '../dynamicCacheKey';
import { PdfPreviewWithTools } from '../../../PdfPreviewWithTools';
import {
  buildFactsheetDownloadFileName,
  resolveFactsheetDownloadAsOf,
} from '../../../../../../utils/documentFileName';

const dialogStyles = {
    dialog: {
        '& .MuiDialog-paper': {
            width: '90vw',
            height: '90vh',
            maxWidth: '1200px',
            maxHeight: '900px',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
        },
    },
    dialogTitle: {
        borderBottom: '1px solid #e5e7eb',
        padding: '16px 24px',
        '& .MuiTypography-root': {
            fontSize: '18px',
            fontWeight: 600,
        },
    },
    dialogContent: {
        padding: '0 !important',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
    },
    pdfContainer: {
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
        background: '#525659',
    },
};

interface PdfPreviewDialogProps {
    open: boolean;
    onClose: () => void;
    data: any;
    template: any;
    title?: string;
    pieChartImages?: { countryChartUrl?: string; sectorChartUrl?: string };
    isin?: string; // ISIN for GraphQL queries
    until?: string; // Optional override for "until" date used in dynamic fetches
}

// Helper function to truncate text with ellipsis
const truncateText = (text: string | undefined, maxLength = 30): string => {
    if (!text || text.length <= maxLength) return text || '';
    return text.substring(0, maxLength - 3) + '...';
};

// PDF Document Component
export const FactsheetPDF: FC<{ data: any, logoSrc?: string, pieChartImages?: { countryChartUrl?: string; sectorChartUrl?: string }, isin?: string, cellDataCache?: Map<string, any>, until?: string, numberFormatLocale?: NumberFormatLocale }> = ({ data, logoSrc, cellDataCache, until, numberFormatLocale: numberFormatLocaleProp }) => {
    const numberFormatLocale = normalizeNumberFormatLocale(numberFormatLocaleProp ?? data?.numberFormatLocale);
    const effectiveData = data || {};
    const effectiveTitle = effectiveData.title || 'Business Owner';
    const effectiveSubtitle = effectiveData.subtitle || 'Ein Fonds für unternehmerisch denkende Anleger';
    const effectiveDate = effectiveData.data_as_of || effectiveData.published_at || 'September 2025';
    const sectionHeaders: SectionHeaders = {
        ...SECTION_HEADER_DEFAULTS,
        ...(effectiveData.sectionHeaders || {}),
    };

    const investmentApproach = effectiveData.investmentApproach || [
        'Wir investieren weltweit in börsennotierten Aktien aus nahezu allen Sektoren',
        'Wir suchen Unternehmen mit dauerhaften Wettbewerbsvorteilen geführt, die von Unternehmern geführt werden, welche ihr Lebenswerk aufbauen',
        'Wir kaufen, wenn der Preis im attraktiven Verhältnis zum Wert steht – oft in Phasen negativer Marktstimmung',
        'Wir investieren mit einem langfristigen Horizont und lassen Unternehmen Zeit, ihr Potenzial zu entfalten',
        'Wir denken unabhängig und handeln oft gegen den Konsens',
        'Wir investieren konzentriert in rund 10 sorgfältig ausgewählte Unternehmen, da exzellente Gelegenheiten selten sind',
        'Wir verstehen uns als engagierte Eigentümer und pflegen einen offenen, konstruktiven Dialog mit dem Management unserer Beteiligungen',
        'Wir informieren unsere Anleger regelmässig und transparent über unsere Philosophie, Portfoliostruktur und Anlageentscheidungen',
    ];

    const top10Stocks = effectiveData.top10Stocks || [
        'Alphabet Inc.', 'Berkshire Hathaway', 'Deutsche Post AG', 'Johnson & Johnson',
        'Microsoft Corp.', 'Nestlé SA', 'Novo Nordisk A/S', 'SAP SE',
        'UBS Group AG', 'Visa Inc.'
    ];

    // Get performance data - support both old and new structure
    const getPerformanceData = () => {
        const perf = effectiveData.performance;
        // New V1-style structure (labels/values/columns)
        if (perf && perf.labels && Array.isArray(perf.labels) && perf.values && Array.isArray(perf.values)) {
            return {
                labels: perf.labels,
                values: perf.values,
                columns: perf.columns || [],
            };
        }
        // Legacy structure (headers/rows)
        if (perf && perf.headers && perf.rows) {
            return {
                labels: perf.headers,
                values: perf.rows.map((row: any) => row.values || []),
                columns: [],
            };
        }
        // Old key-value structure - migrate on the fly
        if (perf && typeof perf === 'object' && !perf.headers && !perf.labels) {
            return {
                labels: ['YTD', '1 y', '3 y', '5 y', '10 y', 'since issue'],
                values: [[
                    perf.ytd || '8,42%',
                    perf['1j'] || '6,28%',
                    perf['3j'] || '18,45%',
                    perf['5j'] || '25,34%',
                    perf['10j'] || '89,15%',
                    perf.auflage || '165,0%'
                ]],
                columns: [],
            };
        }
        // Default
        return {
            labels: ['YTD', '1 y', '3 y', '5 y', '10 y', 'since issue'],
            values: [['8,42%', '6,28%', '18,45%', '25,34%', '89,15%', '165,0%']],
            columns: [],
        };
    };
    const performanceData = getPerformanceData();

    // Helper function to get display value (fetched or static)
    const getDisplayValue = (section: string, field: string, defaultValue: string): string => {
        const value = effectiveData[section]?.[field];
        
        // If it's a dynamic value, try to get from cache
        if (typeof value === 'object' && value?.isDynamic && value?.fields?.length && cellDataCache) {
            const valueKey = dynamicFieldCacheKey(section, field, until, numberFormatLocale);
            const fetchedValue = cellDataCache.get(valueKey);
            if (fetchedValue !== undefined && fetchedValue !== null) {
                return String(fetchedValue);
            }
            // If not in cache yet, return default while loading
            return defaultValue;
        }
        
        // Return static value or default
        return typeof value === 'string' ? value : defaultValue;
    };

    const pricesData = {
        nav: getDisplayValue('prices', 'nav', '1.419,31 EUR'),
        fundVolume: getDisplayValue('prices', 'fundVolume', '507.296.946 EUR')
    };

    const conditionsData = {
        issueSurcharge: getDisplayValue('conditions', 'issueSurcharge', '0%'),
        redemptionFee: getDisplayValue('conditions', 'redemptionFee', '0%'),
        redemptionPeriod: getDisplayValue('conditions', 'redemptionPeriod', '40 Tage zum Quartalsende'),
        ongoingCosts: getDisplayValue('conditions', 'ongoingCosts', '1,09%'),
        managementFee: getDisplayValue('conditions', 'managementFee', '1,00%'),
        performanceFee: getDisplayValue('conditions', 'performanceFee', '10%'),
        hurdleRate: getDisplayValue('conditions', 'hurdleRate', '6% p.a.'),
        highwaterMark: getDisplayValue('conditions', 'highwaterMark', 'Ja'),
        minSubscription: getDisplayValue('conditions', 'minSubscription', '200.000 EUR'),
        incomeUse: getDisplayValue('conditions', 'incomeUse', 'Thesaurierend')
    };

    const masterData = {
        isinWkn: getDisplayValue('masterData', 'isinWkn', 'DE000A0RAAR6 / A0RAAR'),
        fundCurrency: getDisplayValue('masterData', 'fundCurrency', 'EUR'),
        launchDate: getDisplayValue('masterData', 'launchDate', '30.09.2008'),
        initialPrice: getDisplayValue('masterData', 'initialPrice', '100 EUR'),
        fundDomicile: getDisplayValue('masterData', 'fundDomicile', 'Deutschland')
    };

    const fundPartner = effectiveData.fundPartner || {
        custodian: 'DZ Bank AG',
        capitalManagement: 'Axxion S.A.',
        fundAdvisor: 'RV Capital AG'
    };
    const fundPartnerLabels = effectiveData.fundPartnerLabels || DEFAULT_FUND_PARTNER_LABELS;

    const contact = effectiveData.contact || {
        companyName: 'RV Capital AG',
        address: 'Einsiedlerstrasse 23, 8834 Schindellegi, Schweiz',
        email: 'info@rvcapital.ch',
        website: 'www.rvcapital.ch'
    };

    const importantNotes = effectiveData.importantNotes || DEFAULT_IMPORTANT_NOTES;

    return (
        <Document>
            <Page size="A4" style={pdfStyles.page}>
                <View style={pdfStyles.container}>
                    {/* Header */}
                    <View style={pdfStyles.header}>
                        <View style={pdfStyles.headerLeft}>
                            <Text style={[pdfStyles.title, pdfStyles.avenirMarginAdjust]}>{effectiveTitle}</Text>
                            <Text style={[pdfStyles.italicSub, pdfStyles.avenirMarginAdjust]}>{effectiveSubtitle}</Text>
                        </View>
                        <View style={pdfStyles.headerRight}>
                            {logoSrc ? (
                                <Image src={logoSrc} style={{ height: 32, width: 'auto' }} />
                            ) : (
                                <Text style={[pdfStyles.logo, pdfStyles.avenirMarginAdjust]}>Rv Capital</Text>
                            )}
                            <View style={pdfStyles.dateBadge}>
                                <Text>
                                    {moment(effectiveDate, ['YYYY-MM-DD', 'YYYY-MM']).format('DD.MM.YYYY')}
                                </Text>
                            </View>
                        </View>
                    </View>

                    {/* Body */}
                    <View style={pdfStyles.body}>
                        <View style={pdfStyles.twoCol}>
                            {/* Left Column */}
                            <View style={pdfStyles.leftColumn}>
                                {/* Investment Approach */}
                                <View style={pdfStyles.section}>
                                    <Text style={[pdfStyles.avenirMarginAdjust, pdfStyles.sectionHeader]}>{sectionHeaders.investmentApproach}</Text>
                                    <View style={pdfStyles.bulletList}>
                                        {investmentApproach.map((point: string, index: number) => (
                                            <View style={pdfStyles.bulletItemContainer}>
                                                <Text key={index} style={[pdfStyles.avenirMarginAdjust, pdfStyles.bulletItem, ]}>•</Text>
                                                <Text key={index} style={[pdfStyles.avenirMarginAdjust, pdfStyles.bulletItem]}>{point}</Text>
                                            </View>
                                        ))}
                                    </View>
                                </View>

                                {/* Top 10 Stocks */}
                                <View style={[pdfStyles.section]}>
                                    <Text style={[pdfStyles.sectionHeader]}>{sectionHeaders.top10Stocks}</Text>
                                    <View style={pdfStyles.top10Box}>
                                        <View style={pdfStyles.top10Grid}>
                                            {top10Stocks.map((stock: string, index: number) => (
                                                <Text key={index} style={[pdfStyles.top10Item, pdfStyles.avenirMarginAdjust]}>{stock}</Text>
                                            ))}
                                        </View>
                                    </View>
                                </View>
                            </View>

                            {/* Right Column */}
                            <View style={pdfStyles.rightColumn}>
                                {/* Prices */}
                                <View style={[pdfStyles.section]}>
                                    <Text style={[pdfStyles.sectionHeader, { marginBottom: 0 }]}>{sectionHeaders.prices}</Text>
                                    <View style={pdfStyles.kvGroup}>
                                        <View style={pdfStyles.kvRow}>
                                            <Text style={[pdfStyles.kvLabelSingleLine, pdfStyles.avenirMarginAdjust]}>Net Asset Value</Text>
                                            <Text style={[pdfStyles.kvValueSingleLine, pdfStyles.avenirMarginAdjust]}>{truncateText(pricesData.nav, 20)}</Text>
                                        </View>
                                        <View style={pdfStyles.kvRow}>
                                            <Text style={[pdfStyles.kvLabelSingleLine, pdfStyles.avenirMarginAdjust, pdfStyles.kvWidth60]}>Assets under Management</Text>
                                            <Text style={[pdfStyles.kvValueSingleLine, pdfStyles.avenirMarginAdjust, pdfStyles.kvWidth40]}>{truncateText(pricesData.fundVolume, 20)}</Text>
                                        </View>
                                        <View style={pdfStyles.kvRow}>
                                            <Text style={[pdfStyles.kvLabelSingleLine, pdfStyles.kvWidth70]}>Minimum investment</Text>
                                            <Text style={[pdfStyles.kvValueSingleLine, pdfStyles.kvWidth30]}>{truncateText(conditionsData.minSubscription, 20)}</Text>
                                        </View>
                                        <View style={pdfStyles.kvRow}>
                                            <Text style={[pdfStyles.kvLabelSingleLine, pdfStyles.avenirMarginAdjust]}>Divided policy</Text>
                                            <Text style={[pdfStyles.kvValueSingleLine, pdfStyles.avenirMarginAdjust]}>{truncateText(conditionsData.incomeUse, 20)}</Text>
                                        </View>
                                    </View>
                                </View>

                                {/* Conditions */}
                                <View style={[pdfStyles.section]}>
                                    <Text style={[pdfStyles.sectionHeader, { marginBottom: 0 }]}>{sectionHeaders.conditions}</Text>
                                    <View style={pdfStyles.kvGroup}>
                                        <View style={pdfStyles.kvRow}>
                                            <Text style={[pdfStyles.kvLabelSingleLine, pdfStyles.avenirMarginAdjust]}>Initial Sales Fee</Text>
                                            <Text style={[pdfStyles.kvValueSingleLine, pdfStyles.avenirMarginAdjust]}>{truncateText(conditionsData.issueSurcharge, 20)}</Text>
                                        </View>
                                        <View style={pdfStyles.kvRow}>
                                            <Text style={[pdfStyles.kvLabelSingleLine, pdfStyles.avenirMarginAdjust]}>Redemption fee</Text>
                                            <Text style={[pdfStyles.kvValueSingleLine, pdfStyles.avenirMarginAdjust]}>{truncateText(conditionsData.redemptionFee, 20)}</Text>
                                        </View>
                                        <View style={pdfStyles.kvRow}>
                                            <Text style={[pdfStyles.kvLabel, pdfStyles.avenirMarginAdjust]}>Redemption notice period</Text>
                                            <Text style={[pdfStyles.kvValue, pdfStyles.avenirMarginAdjust]}>{conditionsData.redemptionPeriod}</Text>
                                        </View>
                                        <View style={pdfStyles.kvRow}>
                                            <Text style={[pdfStyles.kvLabelSingleLine, pdfStyles.kvWidth70, pdfStyles.avenirMarginAdjust]}>Total Expense Ratio</Text>
                                            <Text style={[pdfStyles.kvValueSingleLine, pdfStyles.kvWidth30, pdfStyles.avenirMarginAdjust]}>{truncateText(conditionsData.ongoingCosts, 20)}</Text>
                                        </View>
                                        <View style={pdfStyles.kvRow}>
                                            <Text style={[pdfStyles.kvLabelSingleLine, pdfStyles.kvWidth70, pdfStyles.avenirMarginAdjust]}>of which management fee</Text>
                                            <Text style={[pdfStyles.kvValueSingleLine, pdfStyles.kvWidth30, pdfStyles.avenirMarginAdjust]}>{truncateText(conditionsData.managementFee, 20)}</Text>
                                        </View>
                                        <View style={pdfStyles.kvRow}>
                                            <Text style={[pdfStyles.kvLabelSingleLine, pdfStyles.avenirMarginAdjust]}>Performance fee</Text>
                                            <Text style={[pdfStyles.kvValueSingleLine, pdfStyles.avenirMarginAdjust]}>{truncateText(conditionsData.performanceFee, 20)}</Text>
                                        </View>
                                        <View style={pdfStyles.kvRow}>
                                            <Text style={[pdfStyles.kvLabelSingleLine, pdfStyles.avenirMarginAdjust]}>Hurdle Rate</Text>
                                            <Text style={[pdfStyles.kvValueSingleLine, pdfStyles.avenirMarginAdjust]}>{truncateText(conditionsData.hurdleRate, 20)}</Text>
                                        </View>
                                        <View style={pdfStyles.kvRow}>
                                            <Text style={[pdfStyles.kvLabelSingleLine, pdfStyles.avenirMarginAdjust]}>Highwater Mark</Text>
                                            <Text style={[pdfStyles.kvValueSingleLine, pdfStyles.avenirMarginAdjust]}>{truncateText(conditionsData.highwaterMark, 20)}</Text>
                                        </View>
                                    </View>
                                </View>

                                {/* Master Data */}
                                <View style={[pdfStyles.section, pdfStyles.marginBottom6]}>
                                    <Text style={[pdfStyles.sectionHeader, pdfStyles.avenirMarginAdjust]}>{sectionHeaders.masterData}</Text>
                                    <View style={pdfStyles.kvGroup}>
                                        <View style={pdfStyles.kvRow}>
                                            <Text style={[pdfStyles.kvLabelSingleLine, pdfStyles.kvWidth30, pdfStyles.avenirMarginAdjust]}>ISIN / WKN</Text>
                                            <Text style={[pdfStyles.kvValueSingleLine, pdfStyles.avenirMarginAdjust]}>{truncateText(masterData.isinWkn, 25)}</Text>
                                        </View>
                                        <View style={pdfStyles.kvRow}>
                                            <Text style={[pdfStyles.kvLabelSingleLine, pdfStyles.avenirMarginAdjust]}>Fund currency</Text>
                                            <Text style={[pdfStyles.kvValueSingleLine, pdfStyles.avenirMarginAdjust]}>{truncateText(masterData.fundCurrency, 20)}</Text>
                                        </View>
                                        <View style={pdfStyles.kvRow}>
                                            <Text style={[pdfStyles.kvLabelSingleLine, pdfStyles.avenirMarginAdjust]}>Inception date</Text>
                                            <Text style={[pdfStyles.kvValueSingleLine, pdfStyles.avenirMarginAdjust]}>{truncateText(masterData.launchDate, 20)}</Text>
                                        </View>
                                        <View style={pdfStyles.kvRow}>
                                            <Text style={[pdfStyles.kvLabelSingleLine, pdfStyles.avenirMarginAdjust]}>Initial issue price</Text>
                                            <Text style={[pdfStyles.kvValueSingleLine, pdfStyles.avenirMarginAdjust]}>{truncateText(masterData.initialPrice, 20)}</Text>
                                        </View>
                                        <View style={pdfStyles.kvRow}>
                                            <Text style={[pdfStyles.kvLabelSingleLine, pdfStyles.avenirMarginAdjust]}>Fund domicile</Text>
                                            <Text style={[pdfStyles.kvValueSingleLine, pdfStyles.avenirMarginAdjust]}>{truncateText(masterData.fundDomicile, 20)}</Text>
                                        </View>
                                    </View>
                                </View>
                            </View>
                        </View>

                        <View style={[pdfStyles.section]}>
                            <Text style={[pdfStyles.avenirMarginAdjust, pdfStyles.sectionHeader]}>{sectionHeaders.performance}</Text>
                            <View style={pdfStyles.perfTable}>
                                <View style={pdfStyles.perfHeader}>
                                    {performanceData.labels.map((label, index) => (
                                        <Text 
                                            key={index} 
                                            style={[
                                                index === 0 ? pdfStyles.perfCellHeaderTimeline : pdfStyles.perfCellHeader, 
                                                pdfStyles.avenirTableMarginAdjust
                                            ]}
                                        >
                                            {label}
                                        </Text>
                                    ))}
                                </View>
                                {performanceData.values.map((row, rowIndex) => (
                                    <View key={rowIndex} style={pdfStyles.perfRow}>
                                        {row.map((cell: any, colIndex: number) => {
                                            const columnType = performanceData.columns?.[colIndex]?.type || 'text';
                                            const isDynamic = cell?.isDynamic && cell?.fields?.length;
                                            const cellKey = dynamicCellCacheKey(rowIndex, colIndex, until, numberFormatLocale);
                                            const fetchedValue = isDynamic && cellDataCache ? cellDataCache.get(cellKey) : null;
                                            
                                            let displayValue = '';
                                            if (isDynamic && fetchedValue !== undefined && fetchedValue !== null) {
                                                // Use fetched value from GraphQL
                                                displayValue = String(fetchedValue);
                                            } else if (columnType === 'text') {
                                                displayValue = typeof cell === 'string' ? cell : (cell?.text || '');
                                            } else if (columnType === 'link') {
                                                displayValue = cell?.linkText || '';
                                            } else if (columnType === 'avatar') {
                                                displayValue = typeof cell === 'string' ? cell : '';
                                            }
                                            return (
                                                <Text 
                                                    key={colIndex} 
                                                    style={[
                                                        colIndex === 0 ? pdfStyles.perfCellTimeline : pdfStyles.perfCell, 
                                                        pdfStyles.perfPositive
                                                    ]}
                                                >
                                                    {displayValue}
                                                </Text>
                                            );
                                        })}
                                    </View>
                                ))}
                            </View>
                        </View>

                        {effectiveData.chartImageDataUrl ? (
                          <View style={[pdfStyles.section, { marginTop: 8 }]}>
                            <Text style={[pdfStyles.sectionHeader]}>{sectionHeaders.chart}</Text>
                            <View style={pdfStyles.historyChart}>
                              <Image src={effectiveData.chartImageDataUrl} style={{ width: '100%' }} />
                            </View>
                          </View>
                        ) : null}

                        {/* Allocation Charts Section */}
                        {/* {(pieChartImages?.countryChartUrl || pieChartImages?.sectorChartUrl) && (
                          <View style={pdfStyles.section}>
                            <View style={pdfStyles.allocationCharts}>
                              {pieChartImages?.countryChartUrl && (
                                <View style={{ width: '50%' }}>
                                  <Text style={pdfStyles.sectionHeader}>{sectionHeaders.countryAllocation}</Text>
                                  <View style={pdfStyles.allocationChartItem}>
                                    <Image src={pieChartImages.countryChartUrl} style={{ width: '80%', height: 'auto' }} />
                                  </View>
                                </View>
                              )}
                              {pieChartImages?.sectorChartUrl && (
                                <View style={{ width: '50%' }}>
                                  <Text style={pdfStyles.sectionHeader}>{sectionHeaders.sectorAllocation}</Text>
                                  <View style={pdfStyles.allocationChartItem}>
                                    <Image src={pieChartImages.sectorChartUrl} style={{ width: '80%', height: 'auto' }} />
                                  </View>
                                </View>
                              )}
                            </View>
                          </View>
                        )} */}

                        {/* Fund Partner and Contact Section */}
                        <View style={[pdfStyles.section, pdfStyles.borderedSection]}>
                            <View style={pdfStyles.fundPartnerContactContainer}>
                                <View style={pdfStyles.fundPartnerColumn}>
                                    <Text style={[pdfStyles.fundPartnerTitle, pdfStyles.avenirMarginAdjust]}>{sectionHeaders.fundPartner}</Text>
                                    <View style={pdfStyles.fundPartnerContent}>
                                        <View style={pdfStyles.fundPartnerRow}>
                                            <Text style={[pdfStyles.fundPartnerLabel, pdfStyles.avenirMarginAdjust]}>{fundPartnerLabels.custodian || DEFAULT_FUND_PARTNER_LABELS.custodian}:</Text>
                                            <Text style={[pdfStyles.fundPartnerValue, pdfStyles.avenirMarginAdjust]}>{fundPartner.custodian}</Text>
                                        </View>
                                        <View style={pdfStyles.fundPartnerRow}>
                                            <Text style={[pdfStyles.fundPartnerLabel, pdfStyles.avenirMarginAdjust]}>{fundPartnerLabels.capitalManagement || DEFAULT_FUND_PARTNER_LABELS.capitalManagement}:</Text>
                                            <Text style={[pdfStyles.fundPartnerValue, pdfStyles.avenirMarginAdjust]}>{fundPartner.capitalManagement}</Text>
                                        </View>
                                        <View style={pdfStyles.fundPartnerRow}>
                                            <Text style={[pdfStyles.fundPartnerLabel, pdfStyles.avenirMarginAdjust]}>{fundPartnerLabels.fundAdvisor || DEFAULT_FUND_PARTNER_LABELS.fundAdvisor}:</Text>
                                            <Text style={[pdfStyles.fundPartnerValue, pdfStyles.avenirMarginAdjust]}>{fundPartner.fundAdvisor}</Text>
                                        </View>
                                    </View>
                                </View>
                                <View style={pdfStyles.contactColumn}>
                                    <Text style={[pdfStyles.contactTitle, pdfStyles.avenirMarginAdjust]}>{sectionHeaders.contact}</Text>
                                    <View style={pdfStyles.contactContent}>
                                        <Text style={[pdfStyles.contactItem, pdfStyles.avenirMarginAdjust]}>{contact.companyName}</Text>
                                        <Text style={[pdfStyles.contactItem, pdfStyles.avenirMarginAdjust]}>{contact.address}</Text>
                                        <Text style={[pdfStyles.contactItem, pdfStyles.avenirMarginAdjust]}>{contact.email}</Text>
                                        <Text style={[pdfStyles.contactItem, pdfStyles.avenirMarginAdjust]}>{contact.website}</Text>
                                    </View>
                                </View>
                            </View>
                        </View>
                    </View>
                </View>
            </Page>

            <Page size="A4" style={[pdfStyles.page, { marginTop: 16 }]}>
                <View style={pdfStyles.body}>

                    {/* Important Notes Section */}
                    <View style={pdfStyles.section}>
                        <Text style={[pdfStyles.sectionHeader, pdfStyles.avenirMarginAdjust]}>{sectionHeaders.importantNotes}</Text>
                        <Text style={[pdfStyles.sectionText, pdfStyles.avenirMarginAdjust]}>
                            {importantNotes}
                        </Text>
                    </View>
                </View>
            </Page>
        </Document>
    );
};

export const PdfPreviewDialog: FC<PdfPreviewDialogProps> = ({
    open,
    onClose,
    data,
    template,
    title = 'PDF Preview',
    pieChartImages,
    isin,
    until: untilOverride,
}) => {
    const [logoSrc, setLogoSrc] = useState<string | undefined>(undefined);
    const [cellDataCache, setCellDataCache] = useState<Map<string, any>>(new Map());

    // Helper function to format date to YYYY-MM-DD for until parameter
    const formatDateToUntil = useCallback((dateStr: string | undefined): string | undefined => {
        if (!dateStr) return undefined;
        // If already YYYY-MM-DD, return as is
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
        // Try to parse and format
        const d = new Date(dateStr);
        if (Number.isNaN(d.getTime())) return undefined;
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }, []);

    const until = formatDateToUntil(
      untilOverride || data?.data_as_of || data?.historyUntil || data?.published_at
    );
    const numberFormatLocale: NumberFormatLocale = normalizeNumberFormatLocale(data?.numberFormatLocale);

    const resolveDynamicIsin = useCallback((config: any): string | undefined => {
        if (isin) return isin;
        if (!config?.fields?.length) return undefined;
        const fieldWithIsin = config.fields.find((field: any) => typeof field?.isin === 'string' && field.isin.trim().length > 0);
        return fieldWithIsin?.isin?.trim();
    }, [isin]);

    useEffect(() => {
        setCellDataCache(new Map());
    }, [until, numberFormatLocale]);

    useEffect(() => {
        let active = true;
        async function load() {
            const url = template?.icon_url as string | undefined;
            if (!url) {
                if (active) setLogoSrc(undefined);
                return;
            }

            try {
                const s3Key = url.replace('https://cixon-finance-docs.s3.amazonaws.com/', '');
                const downloadUrl = `/docs/files/download/${s3Key}`;

                const blob: Blob = await http.getBlob(downloadUrl);
                const reader = new FileReader();
                reader.onloadend = () => {
                    if (active) setLogoSrc(reader.result as string);
                };
                reader.readAsDataURL(blob);
            } catch (error) {
                if (active) setLogoSrc(url);
            }
        }
        load();
        return () => { active = false; };
    }, [template?.icon_url]);

    // Fetch dynamic cell data using GraphQL
    const fetchCellData = useCallback(async (cell: any, rowIndex: number, colIndex: number) => {
        if (!cell?.isDynamic || !cell?.fields?.length) return null;
        const resolvedIsin = resolveDynamicIsin(cell);
        if (!resolvedIsin) return null;
        
        const cellKey = dynamicCellCacheKey(rowIndex, colIndex, until, numberFormatLocale);
        
        // Check cache first
        if (cellDataCache.has(cellKey)) {
            return cellDataCache.get(cellKey);
        }

        try {
            const res = await client.query({
                query: GET_AUTOMATED_COLUMNS,
                variables: { param: { data: [cell], isin: resolvedIsin, until, numberFormatLocale } },
            });
            
            const fetchedValue = res.data.getAutomatedColumns?.[0]?.[0];
            
            // Cache the result - create a new Map to trigger re-render
            setCellDataCache(prev => {
                const newCache = new Map(prev);
                newCache.set(cellKey, fetchedValue);
                return newCache;
            });
            
            return fetchedValue;
        } catch (error) {
            console.error('Failed to fetch cell data:', error);
            return null;
        }
    }, [until, cellDataCache, resolveDynamicIsin, numberFormatLocale]);

    // Fetch dynamic value data using GraphQL
    const fetchDynamicValue = useCallback(async (section: string, field: string, value: any) => {
        if (!value?.isDynamic || !value?.fields?.length) return null;
        const resolvedIsin = resolveDynamicIsin(value);
        if (!resolvedIsin) return null;
        
        const valueKey = dynamicFieldCacheKey(section, field, until, numberFormatLocale);
        
        // Check cache first
        if (cellDataCache.has(valueKey)) {
            return cellDataCache.get(valueKey);
        }

        try {
            const res = await client.query({
                query: GET_AUTOMATED_COLUMNS,
                variables: { param: { data: [value], isin: resolvedIsin, until, numberFormatLocale } },
            });
            
            const fetchedValue = res.data.getAutomatedColumns?.[0]?.[0];
            
            // Cache the result
            setCellDataCache(prev => {
                const newCache = new Map(prev);
                newCache.set(valueKey, fetchedValue);
                return newCache;
            });
            
            return fetchedValue;
        } catch (error) {
            console.error('Failed to fetch dynamic value:', error);
            return null;
        }
    }, [until, cellDataCache, resolveDynamicIsin, numberFormatLocale]);

    // Fetch all dynamic cells and values when dialog opens or data changes
    useEffect(() => {
        if (!open) return;
        
        const fetchAllDynamicData = async () => {
            const promises: Promise<void>[] = [];
            
            // Fetch performance table cells
            if (data?.performance) {
                const getPerformanceData = () => {
                    const perf = data.performance;
                    if (perf && perf.labels && Array.isArray(perf.labels) && perf.values && Array.isArray(perf.values)) {
                        return {
                            labels: perf.labels,
                            values: perf.values,
                            columns: perf.columns || [],
                        };
                    }
                    return { labels: [], values: [], columns: [] };
                };
                
                const performanceData = getPerformanceData();
                
                performanceData.values.forEach((row: any[], rowIndex: number) => {
                    row.forEach((cell: any, colIndex: number) => {
                        if (cell?.isDynamic && cell?.fields?.length) {
                            promises.push(
                                fetchCellData(cell, rowIndex, colIndex).then(() => undefined)
                            );
                        }
                    });
                });
            }
            
            // Fetch prices dynamic values
            ['nav', 'fundVolume'].forEach(field => {
                const value = data?.prices?.[field];
                if (value?.isDynamic && value?.fields?.length) {
                    promises.push(
                        fetchDynamicValue('prices', field, value).then(() => undefined)
                    );
                }
            });
            
            // Fetch conditions dynamic values
            ['issueSurcharge', 'redemptionFee', 'redemptionPeriod', 'ongoingCosts', 
             'managementFee', 'performanceFee', 'hurdleRate', 'highwaterMark', 
             'minSubscription', 'incomeUse'].forEach(field => {
                const value = data?.conditions?.[field];
                if (value?.isDynamic && value?.fields?.length) {
                    promises.push(
                        fetchDynamicValue('conditions', field, value).then(() => undefined)
                    );
                }
            });
            
            // Fetch masterData dynamic values
            ['isinWkn', 'fundCurrency', 'launchDate', 'initialPrice', 'fundDomicile'].forEach(field => {
                const value = data?.masterData?.[field];
                if (value?.isDynamic && value?.fields?.length) {
                    promises.push(
                        fetchDynamicValue('masterData', field, value).then(() => undefined)
                    );
                }
            });
            
            await Promise.all(promises);
        };
        
        fetchAllDynamicData();
    }, [open, data?.performance, data?.prices, data?.conditions, data?.masterData, fetchCellData, fetchDynamicValue, until, numberFormatLocale]);

    const downloadFileName = useMemo(
        () =>
            buildFactsheetDownloadFileName({
                customTemplate: data?.pdf_download_name,
                title: data?.title || template?.fond || template?.name || 'Factsheet',
                asOf: resolveFactsheetDownloadAsOf(data) || until,
            }),
        [data, template?.fond, template?.name, until],
    );

    const factsheetPdf = (
        <FactsheetPDF
            data={data}
            logoSrc={logoSrc}
            pieChartImages={pieChartImages}
            isin={isin}
            cellDataCache={cellDataCache}
            until={until}
            numberFormatLocale={numberFormatLocale}
        />
    );

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={false}
            sx={dialogStyles.dialog}
        >
            <DialogTitle sx={dialogStyles.dialogTitle}>
                <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                    <Box>{title}</Box>
                    <IconButton onClick={onClose} size="small">
                        <Close />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent sx={dialogStyles.dialogContent}>
                <Box sx={dialogStyles.pdfContainer}>
                    {open ? (
                        <PdfPreviewWithTools document={factsheetPdf} fileName={downloadFileName} />
                    ) : null}
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default PdfPreviewDialog;


